import React from 'react';
import { GameState } from '../../domain/entities';
import { View } from '../../App';
import { useGameActions } from '../../contexts/GameActionsContext';
import { useUI } from '../../contexts/UIContext';

import { DashboardScreen } from '../screens/DashboardScreen';
import { CompetitionsScreen } from '../screens/CompetitionsScreen';
import { TeamScreen } from '../screens/TeamScreen';
import { TacticsScreen } from '../screens/TacticsScreen';
import { FinancesScreen } from '../screens/FinancesScreen';
import { TransfersScreen } from '../screens/TransfersScreen';
import { YouthScreen } from '../screens/YouthScreen';
import { ManagerScreen } from '../screens/ManagerScreen';
import { AssistantScreen } from '../screens/AssistantScreen';
import { InboxScreen } from '../screens/InboxScreen';
import { GameScreen } from '../screens/GameScreen';
import { TeamDetailScreen } from '../screens/TeamDetailScreen';
import { PlayerDetailScreen } from '../screens/PlayerDetailScreen';
import { DebugScreen } from '../screens/DebugScreen';
import { TrainingScreen } from '../screens/TrainingScreen';
import { HistoryScreen } from '../screens/HistoryScreen';
import { AiService } from '../../services/AiService';

interface AppRouterProps {
    gameState: GameState;
    currentView: View;
    pushView: (view: View) => void;
    updateCurrentView: (propsToUpdate: Partial<View>) => void;
    isSimulating: boolean;
    onRestartWithTeamCount?: (teamCount: number) => Promise<void>;
    onGoToEndOfSeason?: () => void;
}

export const AppRouter: React.FC<AppRouterProps> = ({ gameState, currentView, pushView, updateCurrentView, isSimulating, onRestartWithTeamCount, onGoToEndOfSeason }) => {
    const gameActions = useGameActions();
    const uiState = useUI();
    
    const playerLeague = gameState.leagues[gameState.playerLeagueName];
    if (!playerLeague) {
        return <div className="panel"><div className="panel-content">Loading league data...</div></div>;
    }
    const playerTeam = playerLeague.teams.find(t => t.name === gameState.playerTeamName);
    if (!playerTeam) {
        return <div className="panel"><div className="panel-content">Loading team data...</div></div>;
    }

    const allPlayersInLeague = playerLeague.teams.flatMap(t => t.squad);
    const leagueAverageSkill = allPlayersInLeague.length > 0
        ? allPlayersInLeague.reduce((sum, p) => sum + p.skill, 0) / allPlayersInLeague.length
        : 50;

    switch(currentView.type) {
        case 'play':
            return <DashboardScreen
                gameState={gameState}
                pushView={pushView}
                onPlayWeek={() => gameActions.playWeek(uiState.isAiPunditEnabled)}
                onGoToEndOfSeason={onGoToEndOfSeason}
                onMarkNewsAsRead={gameActions.markNewsAsRead}
                isSimulating={isSimulating}
                isAiPunditEnabled={uiState.isAiPunditEnabled}
                showRatingsOnMatchReport={uiState.showRatingsOnMatchReport}
                onPlayerConversation={gameActions.handlePlayerConversation}
                onOfferFinancialIncentive={gameActions.offerFinancialIncentive}
                onCacheAiMatchReport={gameActions.cacheAiMatchReport}
                initialTab={currentView.activeTab}
            />;
        case 'inbox':
            return <InboxScreen
                gameState={gameState}
                onMarkNewsAsRead={gameActions.markNewsAsRead}
                pushView={pushView}
            />;
        case 'competitions':
            return <CompetitionsScreen
                gameState={gameState}
                pushView={pushView}
                adsEnabled={uiState.adsEnabled}
                isAiPunditEnabled={uiState.isAiPunditEnabled}
                showRatingsOnMatchReport={uiState.showRatingsOnMatchReport}
                onCacheAiMatchReport={gameActions.cacheAiMatchReport}
                initialLeague={currentView.leagueName}
                activeTab={currentView.activeTab}
            />;
        case 'team':
            return <TeamScreen 
                team={playerTeam} 
                leagueAverageSkill={leagueAverageSkill} 
                season={gameState.season} 
                gameState={gameState} 
                pushView={pushView}
                updateCurrentView={updateCurrentView}
                activeTab={currentView.activeTab}
                adsEnabled={uiState.adsEnabled}
                isAiPunditEnabled={uiState.isAiPunditEnabled}
                showRatingsOnMatchReport={uiState.showRatingsOnMatchReport}
                onPlayerConversation={gameActions.handlePlayerConversation}
                onOfferFinancialIncentive={gameActions.offerFinancialIncentive}
                onCacheAiMatchReport={gameActions.cacheAiMatchReport}
            />;
        case 'tactics':
            return <TacticsScreen
                team={playerTeam}
                onUpdateTactics={gameActions.updateTactics}
                onAddNews={gameActions.addNews}
                adsEnabled={uiState.adsEnabled}
                currentWeek={gameState.currentWeek}
                pushView={pushView}
                gameState={gameState}
            />;
        case 'training':
            return <TrainingScreen
                team={playerTeam}
                pushView={pushView}
                onUpdateTeamTrainingFocus={gameActions.updateTeamTrainingFocus}
                onUpdatePositionalTrainingFocus={gameActions.updatePositionalTrainingFocus}
                onUpdatePlayerTrainingFocus={gameActions.updatePlayerTrainingFocus}
                onUpgradeTrainingFacilities={gameActions.upgradeTrainingFacilities}
                onDowngradeTrainingFacilities={gameActions.downgradeTrainingFacilities}
                adsEnabled={uiState.adsEnabled}
            />;
        case 'transfers':
            return <TransfersScreen
                gameState={gameState}
                playerTeam={playerTeam}
                pushView={pushView}
                updateCurrentView={updateCurrentView}
                onNegotiatePurchase={gameActions.negotiatePurchase}
                onFinalizePurchase={gameActions.finalizePurchase}
                onSubmitFreeAgentBid={gameActions.submitFreeAgentBid}
                onSellPlayer={gameActions.sellPlayer}
                onReleasePlayer={gameActions.releasePlayer}
                adsEnabled={uiState.adsEnabled}
                activeTab={currentView.activeTab}
                filters={currentView.filters}
                filterWeek={currentView.filterWeek}
                filterSeason={currentView.filterSeason}
            />;
        case 'youth':
            return <YouthScreen
                team={playerTeam}
                onUpgradeAcademy={gameActions.upgradeYouthAcademy}
                onDowngradeAcademy={gameActions.downgradeYouthAcademy}
                onPromotePlayer={gameActions.promoteYouthPlayer}
                onUpdateYouthPreferences={gameActions.updateYouthPreferences}
                leagueAverageSkill={leagueAverageSkill}
                adsEnabled={uiState.adsEnabled}
                currentWeek={gameState.currentWeek}
            />;
        case 'finances':
            return <FinancesScreen team={playerTeam} currentWeek={gameState.currentWeek} season={gameState.season} adsEnabled={uiState.adsEnabled} />;
        case 'assistant':
            return <AssistantScreen
                gameState={gameState}
                onUpdateGameState={(updates) => {
                    // Update game state with the new values
                    Object.assign(gameState, updates);
                }}
                season={currentView.season}
                week={currentView.week}
            />;
        case 'manager':
            return <ManagerScreen 
                gameState={gameState}
                playerTeam={playerTeam}
                pushView={pushView}
                adsEnabled={uiState.adsEnabled}
                onUpdateManagerName={gameActions.updateManagerName}
            />;
        case 'game':
            return <GameScreen 
                gameState={gameState}
                onReturnToStart={gameActions.resetAndGoToStart}
                onHardReset={gameActions.hardResetGame}
                onToggleDebug={uiState.toggleDebug}
                listSaves={gameActions.listSaves}
                onLoadNamedGame={gameActions.loadNamedGame}
                onSaveNamedGame={gameActions.saveNamedGame}
                onDeleteNamedGame={gameActions.deleteNamedGame}
            />;
        case 'teamDetail':
            return <TeamDetailScreen
                key={currentView.teamName}
                teamName={currentView.teamName}
                gameState={gameState}
                pushView={pushView}
                updateCurrentView={updateCurrentView}
                activeTab={currentView.activeTab}
                adsEnabled={uiState.adsEnabled}
                isAiPunditEnabled={uiState.isAiPunditEnabled}
                showRatingsOnMatchReport={uiState.showRatingsOnMatchReport}
                showHiddenInfo={gameState.debugFlags?.showHiddenInfo}
                onCacheAiMatchReport={gameActions.cacheAiMatchReport}
            />;
        case 'playerDetail':
            return <PlayerDetailScreen 
                key={currentView.playerId} 
                playerId={currentView.playerId}
                gameState={gameState}
                pushView={pushView}
                updateCurrentView={updateCurrentView}
                adsEnabled={uiState.adsEnabled}
                onUpdatePlayerTrainingFocus={gameActions.updatePlayerTrainingFocus}
                onRenewContract={gameActions.renewContract}
                onNegotiatePurchase={gameActions.negotiatePurchase}
                onFinalizePurchase={gameActions.finalizePurchase}
                onSellPlayer={gameActions.sellPlayer}
                onUnlistPlayer={gameActions.unlistPlayer}
                onReleasePlayer={gameActions.releasePlayer}
                isAiPunditEnabled={uiState.isAiPunditEnabled}
                showRatingsOnMatchReport={uiState.showRatingsOnMatchReport}
                onPlayerConversation={gameActions.handlePlayerConversation}
                onOfferFinancialIncentive={gameActions.offerFinancialIncentive}
                onUpdateTactics={gameActions.updateTactics}
                onCacheAiPunditTake={gameActions.cacheAiPunditTake}
                onCacheAiMatchReport={gameActions.cacheAiMatchReport}
                onCacheAiPlayerRoleAnalysis={gameActions.cacheAiPlayerRoleAnalysis}
                showHiddenInfo={gameState.debugFlags?.showHiddenInfo}
            />;
        case 'history':
            return <HistoryScreen gameState={gameState} />;
        case 'debug':
            return uiState.showDebug ? <DebugScreen
                gameState={gameState}
                onSimulateWeek={() => gameActions.playWeek(uiState.isAiPunditEnabled)}
                onSimulateFourWeeks={() => gameActions.simulateFourWeeks()}
                onSimulateSeason={() => gameActions.simulateFullSeason()}
                onSimulateToCupFinal={() => gameActions.simulateToCupFinal()}
                onSimulateToSeasonWin={() => gameActions.simulateToSeasonWin()}
                isSimulating={isSimulating}
                onImportGameState={gameActions.loadFullGameState}
                onToggleAds={uiState.toggleAds}
                adsEnabled={uiState.adsEnabled}
                onToggleAiPundit={uiState.toggleAiPundit}
                isAiPunditEnabled={uiState.isAiPunditEnabled}
                onToggleShowRatingsOnMatchReport={uiState.toggleShowRatingsOnMatchReport}
                showRatingsOnMatchReport={uiState.showRatingsOnMatchReport}
                onToggleDebugFlag={gameActions.toggleDebugFlag}
                onAddMoney={() => gameActions.addPlayerMoney(1000000)}
                onAddBigMoney={() => gameActions.addPlayerMoney(100000000)}
                onAddElitePlayer={gameActions.addElitePlayer}
                onAddLegendPlayer={gameActions.addLegendPlayer}
                onGenerateGreatYouthIntake={gameActions.generateGreatYouthIntake}
                onBoostTeamMorale={gameActions.boostTeamMorale}
                onHealAllInjuries={gameActions.healAllInjuries}
                isAiAvailable={AiService.isAiAvailable()}
                onRestartWithTeamCount={onRestartWithTeamCount || gameActions.restartWithTeamCount}
            /> : null;
        default:
            return <div>Select a view</div>;
    }
};